/* eslint-disable arrow-body-style */
import { Box, Collapse, Grid2, IconButton, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpandMore } from '@mui/icons-material';
import { useForm, useFormState } from 'react-final-form';
import InputField from '../../../components/InputField';

export default function InvoiceAddress({ details }) {
  const { t } = useTranslation('checkout');
  const [extraOpen, setExtraOpen] = useState(false);
  const hasPrefilledInvoiceAddressRef = useRef(false);
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const showAddressTypeSelector = Boolean(details?.event?.frontendSettings?.invoiceAddressTypeSelector);
  const selectedAddressType = values?.invoiceAddress?.addressType;
  const showCompanyField = !showAddressTypeSelector || selectedAddressType === 'company';
  const isCompanyRequired = showAddressTypeSelector && selectedAddressType === 'company';

  useEffect(() => {
    if (hasPrefilledInvoiceAddressRef.current) return;

    const invoiceAddress = values?.invoiceAddress || {};
    const invoiceAddressHasUserData = ['forename', 'name', 'company', 'emailAddress'].some((field) => Boolean(invoiceAddress[field]));
    if (invoiceAddressHasUserData) return;

    const firstLineItem = details?.products
      ?.find((product) => Number(values?.[`product_${product.id}`]) > 0);
    const firstAttendee = firstLineItem
      ? (values?.[`productAttendee_${firstLineItem.id}`] || [])[0]
      : undefined;

    if (!firstAttendee) return;

    let copiedAnyValue = false;

    ['forename', 'name', 'company', 'emailAddress'].forEach((field) => {
      if (!firstAttendee[field]) return;
      copiedAnyValue = true;
      form.change(`invoiceAddress.${field}`, firstAttendee[field]);
    });

    if (copiedAnyValue) {
      hasPrefilledInvoiceAddressRef.current = true;
    }
  }, [details?.products, form, values]);

  useEffect(() => {
    if (!showAddressTypeSelector) return;
    if (selectedAddressType !== 'private') return;
    if (!values?.invoiceAddress?.company) return;
    form.change('invoiceAddress.company', undefined);
  }, [form, selectedAddressType, showAddressTypeSelector, values?.invoiceAddress?.company]);

  const fields = useMemo(() => [
    ...(showAddressTypeSelector ? [{
      name: 'invoiceAddress.addressType',
      label: t('invoiceAddress.addressTypeTitle'),
      type: 'select',
      required: true,
      gridSize: { xs: 12 },
      options: [
        { value: 'company', name: t('invoiceAddress.addressTypeCompany') },
        { value: 'private', name: t('invoiceAddress.addressTypePrivate') },
      ],
    }] : []),
    ...(showCompanyField ? [{
      name: 'invoiceAddress.company',
      label: t('invoiceAddress.company'),
      required: isCompanyRequired,
      gridSize: { xs: 12 },
    }] : []),
    {
      name: 'invoiceAddress.forename',
      label: t('invoiceAddress.forename'),
      required: true,
      gridSize: { xs: 12, md: 6 },
    },
    {
      name: 'invoiceAddress.name',
      label: t('invoiceAddress.name'),
      required: true,
      gridSize: { xs: 12, md: 6 },
    },
    {
      name: 'invoiceAddress.address1',
      label: t('invoiceAddress.address'),
      required: true,
      gridSize: { xs: 12 },
    },
    {
      name: 'invoiceAddress.postalCode',
      label: t('invoiceAddress.postalCode'),
      required: true,
      gridSize: { xs: 12, md: 3 },
    },
    {
      name: 'invoiceAddress.city',
      label: t('invoiceAddress.city'),
      required: true,
      gridSize: { xs: 12, md: 9 },
    },
    {
      name: 'invoiceAddress.country',
      label: t('invoiceAddress.country'),
      type: 'country',
      required: true,
      gridSize: { xs: 12, md: 12 },
    },
    {
      name: 'invoiceAddress.emailAddress',
      label: t('invoiceAddress.emailAddress'),
      type: 'email',
      required: true,
      gridSize: { xs: 12 },
    },
  ], [isCompanyRequired, showAddressTypeSelector, showCompanyField, t]);

  const configuredInvoiceAddressFields = details?.event?.frontendSettings?.invoiceAddressFields;
  const hasInvoiceAddressFieldConfig = Boolean(configuredInvoiceAddressFields)
    && typeof configuredInvoiceAddressFields === 'object';

  const extraFields = useMemo(() => {
    const configuredFields = [
      {
        code: 'leitwegId',
        name: 'invoiceAddress.leitwegId',
        label: t('invoiceAddress.extraFields.leitwegId.label'),
        gridSize: { xs: 12 },
        helperText: t('invoiceAddress.extraFields.leitwegId.helperText'),
      },
      {
        code: 'projectNumber',
        name: 'invoiceAddress.projectNumber',
        label: t('invoiceAddress.extraFields.projectNumber.label'),
        gridSize: { xs: 12, md: 6 },
      },
      {
        code: 'costCenter',
        name: 'invoiceAddress.costCenter',
        label: t('invoiceAddress.extraFields.costCenter.label'),
        gridSize: { xs: 12, md: 6 },
      },
    ];

    const visibleFields = configuredFields.filter((field) => {
      if (!hasInvoiceAddressFieldConfig) return true;
      return Boolean(configuredInvoiceAddressFields?.[field.code]);
    });

    if (visibleFields.length === 1) {
      return visibleFields.map((field) => ({ ...field, gridSize: { xs: 12, md: 12 } }));
    }

    if (visibleFields.length === 2 && visibleFields.some((field) => field.code === 'leitwegId')) {
      return visibleFields.map((field) => ({ ...field, gridSize: { xs: 12, md: 12 } }));
    }

    return visibleFields;
  }, [configuredInvoiceAddressFields, hasInvoiceAddressFieldConfig, t]);

  const extraFieldsTitle = useMemo(() => {
    if (extraFields.length === 1) return extraFields[0].label;
    return t('invoiceAddress.extraFields.title');
  }, [extraFields, t]);

  const extraFieldsSummary = useMemo(
    () => extraFields.map((field) => field.label).join(', '),
    [extraFields],
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ marginBottom: 4 }}>
        {t('steps.invoiceAddress')}
      </Typography>
      <Grid2 container spacing={2}>
        {fields.map((field) => (
          <InputField key={field.name} {...field} />
        ))}
      </Grid2>

      {/* ── Additional billing details ── */}
      {extraFields.length > 0 && (
        <Box
          sx={{
            mt: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            onClick={() => setExtraOpen((v) => !v)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              userSelect: 'none',
              bgcolor: extraOpen ? 'action.hover' : 'transparent',
              transition: 'background-color 150ms',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {extraFieldsTitle}
              </Typography>
              {!extraOpen && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {extraFieldsSummary}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              disableRipple
              sx={{
                transform: extraOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 200ms',
                color: 'text.secondary',
              }}
            >
              <ExpandMore fontSize="small" />
            </IconButton>
          </Box>

          <Collapse in={extraOpen}>
            <Box sx={{ px: 2, pt: 1.5, pb: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Grid2 container spacing={2}>
                {extraFields.map((field) => (
                  <InputField key={field.name} {...field} />
                ))}
              </Grid2>
            </Box>
          </Collapse>
        </Box>
      )}

      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
        * Pflichtfeld
      </Typography>
    </Box>
  );
}
